import { StateError } from '../errors';
import { rngInt, type RngHolder } from '../math/rng';
import { carriesDisc, type GameDef } from '../engine/gameDef';
import type { GameConfigShape } from '../config/gameConfig';
import { hexNeighbors, type HexCoord } from './axial';
import { RANDOM_PORT, WATER_POOL_KEY } from './seaSchema';
import type { MapDefJson, MapFogJson, MapPoolJson } from './mapSchema';
import type { ResolvedMap } from './graph';

// The SEA resolver (H20-S1, plan §4.9) — W1–W4, run only for a map with a `sea` block (a
// classic map never reaches this file, so its draws and its ResolvedMap are byte-identical):
//
//   W1  harbour KINDS: Fisher–Yates over each harbour region's listed kinds (sorted region
//       order; an unlabelled harbour is the '' region, i.e. map-wide), onto FIXED positions.
//   W2  terrain, per region in SORTED region-id order: FY over the region's pool expanded in
//       canonical key order (the reserved `water` key included), dealt to the region's random
//       cells in tuple order (r, then q). A cell dealt `water` leaves the land set.
//   W3  discs, per region in sorted order: FY over the region's disc pool (ascending), dealt to
//       the cells W2 made numbered land (and fixed cells asking 'auto'), in tuple order.
//   W4  rejection by the footprint's OWN constraints — red spacing, red on named tiles, poor
//       numbers on named tiles — repeating W2–W3 with fresh draws; the harbours stand.
//       Bounded by `mapGenRejectBound`.
//
// A fully fixed sea map draws nothing at all (T-SEA-005). The fog stack is NOT dealt here:
// its order is secret, so it is shuffled into a server-private bag (W5/W6, the sea-fog module)
// and only its multiset rides the ResolvedMap.

/** Fisher–Yates from the end (i = n−1..1, j = rngInt(i+1)) — the normative shuffle (generator.ts). */
function shuffle<T>(arr: T[], rng: RngHolder): void {
  for (let i = arr.length - 1; i >= 1; i--) {
    const j = rngInt(rng, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

/** A count table as a flat list in CANONICAL key order (D25: never trust insertion order). */
function expand(counts: Readonly<Record<string, number>>): string[] {
  const out: string[] = [];
  for (const k of Object.keys(counts).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))) for (let i = 0; i < (counts[k] ?? 0); i++) out.push(k);
  return out;
}

/** The fog's public multisets (never an order); `pools` only where an editor map has them. */
function resolvedFog(fog: MapFogJson): MapFogJson {
  const pool = (p: MapPoolJson): MapPoolJson => ({ tiles: { ...p.tiles }, discs: [...p.discs].sort((a, b) => a - b) });
  const out: MapFogJson = { cells: fog.cells.map((c) => ({ ...c })), pool: pool(fog.pool) };
  if (fog.pools !== undefined) out.pools = Object.fromEntries(Object.entries(fog.pools).map(([k, p]) => [k, pool(p)]));
  return out;
}

const tupleOrder = (a: HexCoord, b: HexCoord): number => a.r - b.r || a.q - b.q;
const cellKey = (c: HexCoord): string => `${c.q},${c.r}`;

export function resolveSeaMap(def: GameDef, cfg: GameConfigShape, rng: RngHolder, json: MapDefJson, base: ResolvedMap): ResolvedMap {
  const sea = json.sea!;
  const tileDef = new Map(def.tiles.map((t) => [t.id, t]));
  const numberedTile = (id: string): boolean => {
    const t = tileDef.get(id);
    return t !== undefined && carriesDisc(t);
  };
  const resolvedSea = (dealtWater: HexCoord[]): NonNullable<ResolvedMap['sea']> => ({
    waters: [...sea.waters.map((w) => ({ ...w })), ...dealtWater.map((c) => ({ q: c.q, r: c.r }))],
    frameEdges: sea.frameEdges.map((e) => ({ ...e })),
    fog: sea.fog ? resolvedFog(sea.fog) : null,
    setupIslands: sea.setupIslands ? sea.setupIslands.map((c) => ({ ...c })) : null,
    landfallVp: sea.landfallVp,
    banditStart: typeof sea.banditStart === 'string' ? sea.banditStart : { ...sea.banditStart },
    pirateStart: sea.pirateStart ? { ...sea.pirateStart } : null,
    rules: { ...sea.rules, goldYield: { ...sea.rules.goldYield } },
    constraints: {
      redSpacing: sea.constraints.redSpacing,
      forbidRedOn: [...sea.constraints.forbidRedOn],
      poorNumbers: sea.constraints.poorNumbers ? { tiles: [...sea.constraints.poorNumbers.tiles], numbers: [...sea.constraints.poorNumbers.numbers] } : null,
    },
  });

  // an editor map's random ports are dealt even when every tile and disc is fixed; a shipped
  // fully-fixed map draws nothing at all (T-SEA-005)
  const custom = json.custom !== undefined;
  const randomPorts = custom && json.harbors.some((h) => h.kind === RANDOM_PORT);
  const dealing = json.hexes.some((h) => h.tile === 'random' || h.disc === 'auto') || randomPorts;
  if (!dealing) {
    // fully fixed: no draws at all
    return { ...base, hexes: json.hexes.map((h) => ({ q: h.q, r: h.r, tile: h.tile, disc: h.disc === 'auto' ? null : h.disc })), sea: resolvedSea([]) };
  }

  // W1: harbour kinds, per harbour region (sorted), over the fixed positions. An editor map
  // instead deals its 'random' ports from the top of its shuffled port deck, in listing order;
  // its fixed ports never move.
  const harbors = base.harbors.map((h) => ({ ...h }));
  if (custom) {
    const deck = [...(json.custom!.portPool ?? [])].sort();
    shuffle(deck, rng);
    let next = 0;
    for (const h of harbors) if (h.kind === RANDOM_PORT) h.kind = deck[next++]!;
  }
  const harborRegions = custom ? [] : [...new Set(json.harbors.map((h) => h.region ?? ''))].sort();
  for (const region of harborRegions) {
    const idx = json.harbors.map((h, i) => ((h.region ?? '') === region ? i : -1)).filter((i) => i >= 0);
    const kinds = idx.map((i) => harbors[i]!.kind);
    shuffle(kinds, rng);
    idx.forEach((i, j) => (harbors[i]!.kind = kinds[j]!));
  }

  const regionOf = (h: { region?: string }): string => h.region ?? '';
  const regions = [...new Set(json.hexes.filter((h) => h.tile === 'random' || h.disc === 'auto').map(regionOf))].sort();
  const pools = json.randomPools?.regions ?? {};
  const bound = cfg.engine.mapGenRejectBound;
  const red = new Set(cfg.engine.redNumbers);
  const forbidRed = new Set(sea.constraints.forbidRedOn);
  const poor = sea.constraints.poorNumbers;

  for (let attempt = 0; attempt < bound; attempt++) {
    const tileAt = new Map<string, string>();
    for (const h of json.hexes) if (h.tile !== 'random') tileAt.set(cellKey(h), h.tile);
    // W2: terrain per region
    for (const region of regions) {
      const cells = json.hexes.filter((h) => h.tile === 'random' && regionOf(h) === region).sort(tupleOrder);
      const pool = expand(pools[region]?.tiles ?? {});
      if (custom ? pool.length < cells.length : pool.length !== cells.length) throw new StateError(`sea map: region '${region}' pools ${pool.length} tiles for ${cells.length} cells`);
      shuffle(pool, rng);
      cells.forEach((c, i) => tileAt.set(cellKey(c), pool[i]!));
    }
    // W3: discs per region, to what became numbered land
    const discAt = new Map<string, number>();
    for (const h of json.hexes) if (typeof h.disc === 'number') discAt.set(cellKey(h), h.disc);
    for (const region of regions) {
      const cells = json.hexes
        .filter((h) => h.disc === 'auto' && regionOf(h) === region && tileAt.get(cellKey(h)) !== WATER_POOL_KEY && numberedTile(tileAt.get(cellKey(h))!))
        .sort(tupleOrder);
      const pool = [...(pools[region]?.discs ?? [])].sort((a, b) => a - b);
      if (custom ? pool.length < cells.length : pool.length !== cells.length) throw new StateError(`sea map: region '${region}' pools ${pool.length} discs for ${cells.length} numbered cells`);
      shuffle(pool, rng);
      cells.forEach((c, i) => discAt.set(cellKey(c), pool[i]!));
    }
    const land = json.hexes
      .filter((h) => tileAt.get(cellKey(h)) !== WATER_POOL_KEY)
      .map((h) => ({ q: h.q, r: h.r, tile: tileAt.get(cellKey(h))!, disc: discAt.get(cellKey(h)) ?? null }));
    // W4: this footprint's own constraints
    // an editor map's PINNED discs are the author's choice: only what the deal placed can break a rule
    const pinned = custom ? new Set(json.hexes.filter((h) => typeof h.disc === 'number').map(cellKey)) : new Set<string>();
    if (violates(land, red, sea.constraints.redSpacing, forbidRed, poor, pinned)) continue;
    const dealtWater = json.hexes.filter((h) => tileAt.get(cellKey(h)) === WATER_POOL_KEY).sort(tupleOrder);
    return { ...base, harbors, hexes: land, sea: resolvedSea(dealtWater) };
  }
  throw new StateError(`map generation exhausted the rejection bound (${bound}) — layout constraints unsatisfiable`);
}

function violates(
  land: ReadonlyArray<{ q: number; r: number; tile: string; disc: number | null }>,
  red: ReadonlySet<number>,
  redSpacing: boolean,
  forbidRed: ReadonlySet<string>,
  poor: { tiles: string[]; numbers: number[] } | null,
  pinned: ReadonlySet<string>,
): boolean {
  const dealt = (h: { q: number; r: number }): boolean => !pinned.has(cellKey(h));
  const redCells = land.filter((h) => h.disc !== null && red.has(h.disc));
  if (redCells.some((h) => dealt(h) && forbidRed.has(h.tile))) return true;
  if (poor && land.some((h) => dealt(h) && h.disc !== null && poor.tiles.includes(h.tile) && poor.numbers.includes(h.disc))) return true;
  if (redSpacing) {
    const redKeys = new Set(redCells.map(cellKey));
    for (const c of redCells) for (const n of hexNeighbors(c)) if (redKeys.has(cellKey(n)) && (dealt(c) || dealt(n))) return true;
  }
  return false;
}
